// Sugar Alarm — cycle 452: Log, opened OVER `/now` while alarm audio sounds.
//
// 451.1's pin holds the app on `/now` while sustained audio sounds, which made
// Log unreachable at exactly the moment a caregiver wants to record the carbs
// they are giving. This renders the REAL `LogScreen` — its tabs, forms,
// validation, Insulin default, `kind`/`tab` intents, one-shot confirmation
// and 451.5 meal-bolus rules — over Now, with the alarm controls docked on
// top, and NO route change: the URL stays `/now`, so the pin never fires and
// Back can never land in a loop.
//
// ⚠ IT PERSISTS NOTHING AND ADDS NO URL STATE. The intent is a prop; the only
// history effect is one same-URL entry pushed by the opener (so Android Back
// closes this and never navigates), owned and popped by the shell.
//
// ⚠ NO ALARM LOGIC LIVES HERE. The docked control is handed in already
// decided (`dock`); this component only places it above everything else it
// draws, so no Log modal, sheet or keyboard can cover it.

import type { KeyboardEvent, ReactNode } from "react";
import { useEffect, useRef } from "react";

import type { FrameVariant } from "../../hooks/useFrameVariant";
import { LogScreen } from "./LogScreen";
import styles from "./LogOverlay.module.css";

export interface LogOverlayProps {
  variant: FrameVariant;
  /** The query part of the `/log?…` target the Now affordance would have
   *  navigated to (`""` or `"?tab=insulin&basal=1"`). */
  search: string;
  /** Sustained alarm audio is sounding right now. */
  alarmSounding: boolean;
  /** The docked `CompactAlarmControl`, or null once nothing is owed. */
  dock: ReactNode;
  onClose: () => void;
}

export function LogOverlay({
  variant,
  search,
  alarmSounding,
  dock,
  onClose,
}: LogOverlayProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // `aria-modal` is a promise: keep Tab inside the sheet so a keyboard or
  // switch user cannot land on the Now controls hidden beneath it.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const root = rootRef.current;
    if (!root) return;
    const focusables = Array.from(
      root.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.getClientRects().length > 0 || el === document.activeElement);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (first == null || last == null) return;
    const active = document.activeElement as HTMLElement | null;
    const inside = active != null && root.contains(active);
    if (e.shiftKey) {
      if (!inside || active === first) {
        e.preventDefault();
        last.focus();
      }
    } else if (!inside || active === last) {
      e.preventDefault();
      first.focus();
    }
  };
  // Move focus into the overlay once, so a keyboard / switch user lands on it
  // rather than on the Now controls beneath.
  useEffect(() => {
    closeRef.current?.focus?.();
  }, []);

  // `search` is read by LogScreen's initial state only, like the URL it
  // stands in for; keeping the element stable across renders is what keeps
  // an unsaved entry when the audio stops.
  const overlay = { search, alarmSounding };

  return (
    <div
      ref={rootRef}
      onKeyDown={onKeyDown}
      className={`${styles.root} ${variant === "kiosk" ? styles.kiosk : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Log"
      data-testid="log-overlay"
    >
      {dock}
      <div className={styles.bar}>
        <button
          ref={closeRef}
          type="button"
          className={styles.close}
          data-testid="log-overlay-close"
          aria-label="Close Log"
          onClick={onClose}
        >
          Close
        </button>
      </div>
      <div className={styles.body}>
        <LogScreen variant={variant} overlay={overlay} />
      </div>
    </div>
  );
}
